import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { normalizeFieldPersonnelRole, type FieldPersonnelRole } from '../utils/fieldPersonnelHelpers';
import { isFieldTokenExpired } from '../utils/fieldSessionUtils';

interface FieldUser {
    id: number;
    name: string;
    phone: string;
    organizationId: number;
    organizationName: string;
    userType: FieldPersonnelRole;
    expertiseClass?: string;
    branchCodes?: string[];
    branches?: Array<{ city: string; cityCode?: string }>;
}

interface FieldAuthContextType {
    user: FieldUser | null;
    isAuthenticated: boolean;
    login: (organizationName: string | undefined, phone: string, password: string) => Promise<FieldUser>;
    registerIndividual: (data: any) => Promise<void>;
    logout: () => void;
    isLogoutConfirmOpen: boolean;
    requestLogout: () => void;
    cancelLogout: () => void;
    confirmLogout: () => void;
    loading: boolean;
    forcePasswordChange: { token: string; required: boolean } | null;
}

const FieldAuthContext = createContext<FieldAuthContextType | undefined>(undefined);

export const useFieldAuth = () => {
    const context = useContext(FieldAuthContext);
    if (!context) {
        throw new Error('useFieldAuth must be used within FieldAuthProvider');
    }
    return context;
};

interface FieldAuthProviderProps {
    children: ReactNode;
}

export const FieldAuthProvider: React.FC<FieldAuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<FieldUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [forcePasswordChange, setForcePasswordChange] = useState<{ token: string; required: boolean } | null>(null);

    useEffect(() => {
        const storedUser = localStorage.getItem('fieldUser');
        const token = localStorage.getItem('fieldToken');
        if (storedUser) {
            if (!token || isFieldTokenExpired(token)) {
                localStorage.removeItem('fieldUser');
                localStorage.removeItem('fieldToken');
                setLoading(false);
                return;
            }
            try {
                const parsed = JSON.parse(storedUser) as FieldUser;
                parsed.userType = normalizeFieldPersonnelRole(parsed.userType as string | undefined);
                setUser(parsed);
                localStorage.setItem('fieldUser', JSON.stringify(parsed));
            } catch (error) {
                console.error('Failed to parse stored user:', error);
                localStorage.removeItem('fieldUser');
                localStorage.removeItem('fieldToken');
            }
        }
        setLoading(false);
    }, []);

    const login = async (organizationName: string | undefined, phone: string, password: string) => {
        try {
            setLoading(true);

            // Use relative path to leverage the proxy (configured in package.json)
            console.log('Login attempting to connect to: /api/mobile/login');

        const response = await fetch('/api/mobile/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                organizationName: organizationName ? organizationName.trim().toLowerCase() : undefined,
                phone: phone, // Send raw value to allow usernames
                password,
            }),
        });

            if (!response.ok) {
                const errorData = await response.json();

                if (response.status === 403 && errorData.code === 'MUST_CHANGE_PASSWORD') {
                    setForcePasswordChange({
                        token: errorData.tempToken,
                        required: true
                    });
                    throw new Error('MUST_CHANGE_PASSWORD');
                }

                throw new Error(errorData.message || 'Giriş başarısız');
            }

            const data = await response.json();
            
            // Block non-expert/doctor/admin/coordinator/auditor users from field login (API bazen type / userType ve farklı casing döner)
            const role = normalizeFieldPersonnelRole(data.personnel.userType || data.personnel.type);
            if (role !== 'expert' && role !== 'doctor' && role !== 'admin' && role !== 'coordinator' && role !== 'auditor' && role !== 'arsiv') {
                throw new Error('Bu sayfadan sadece Uzman/Hekim/Koordinatör/Denetçi/Admin/Arşiv kullanıcıları giriş yapabilir.');
            }

            const orgBranches = data.organization?.branches || data.personnel?.branches || [];
            const userData: FieldUser = {
                id: data.personnel.id,
                name: `${data.personnel.firstName} ${data.personnel.lastName}`,
                phone: data.personnel.phone,
                organizationId: data.organization.id,
                organizationName: data.organization.name,
                userType: role,
                expertiseClass: data.personnel.expertiseClass,
                branchCodes: data.personnel.branchCodes || [],
                branches: orgBranches,
            };

            setUser(userData);
            localStorage.setItem('fieldUser', JSON.stringify(userData));

            if (data.token) {
                localStorage.setItem('fieldToken', data.token);
            }
            return userData;
        } catch (error: any) {
            console.error('Login error:', error);
            localStorage.removeItem('fieldUser');
            localStorage.removeItem('fieldToken');
            throw new Error(error.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
        } finally {
            setLoading(false);
        }
    };

    const registerIndividual = async (registrationData: any) => {
        try {
            setLoading(true);
            const response = await fetch('/api/register-individual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(registrationData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Kayıt başarısız');
            }
        } catch (error: any) {
            console.error('Registration error:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

    const logout = () => {
        setIsLogoutConfirmOpen(false);
        setUser(null);
        setForcePasswordChange(null);
        localStorage.removeItem('fieldUser');
        localStorage.removeItem('fieldToken');
        try {
            sessionStorage.removeItem('admin_mobile_dashboard_cache');
            sessionStorage.removeItem('field_dashboard_cache');
        } catch (e) {}
    };

    const requestLogout = () => {
        setIsLogoutConfirmOpen(true);
    };

    const cancelLogout = () => {
        setIsLogoutConfirmOpen(false);
    };

    const confirmLogout = () => {
        logout();
    };

    const value: FieldAuthContextType = {
        user,
        isAuthenticated: !!user,
        login,
        registerIndividual,
        logout,
        isLogoutConfirmOpen,
        requestLogout,
        cancelLogout,
        confirmLogout,
        loading,
        forcePasswordChange
    };

    return (
        <FieldAuthContext.Provider value={value}>
            {children}
        </FieldAuthContext.Provider>
    );
};
